import React, { useEffect, useMemo, useState } from 'react';
import Modal from 'components/ui/Modal';
import Button from 'components/ui/Button';
import Select from 'components/ui/Select';
import DatePicker from 'components/ui/DatePicker';
import { INSTALACIONES } from 'lib/auditoriaInstalaciones';
import { compararCodigo } from 'lib/arboles';
import { prepararLinea, lineaAFormulario } from 'lib/cables';
import { hoyCR } from 'lib/fechaCR';
import { fetchArbolesEstado } from 'services/arbolesService';
import { Campo, Texto } from 'pages/arboles/Campos';
import { mensajeError } from 'lib/errores';

const Grupo = ({ titulo, children }) => (
  <fieldset className="grid gap-3 sm:grid-cols-2">
    <legend className="mb-1 text-xs font-bold uppercase tracking-wide text-muted-foreground">{titulo}</legend>
    {children}
  </fieldset>
);

// Árboles de la instalación para elegir de dónde sale y adónde llega el cable.
// Si no se pueden leer, los campos quedan deshabilitados con el aviso (el
// resto de la ficha se guarda igual).
function useArboles(open) {
  const [estado, setEstado] = useState({ cargando: false, error: false, filas: [] });
  useEffect(() => {
    if (!open) return undefined;
    let vivo = true;
    setEstado({ cargando: true, error: false, filas: [] });
    fetchArbolesEstado()
      .then((filas) => { if (vivo) setEstado({ cargando: false, error: false, filas: filas || [] }); })
      .catch(() => { if (vivo) setEstado({ cargando: false, error: true, filas: [] }); });
    return () => { vivo = false; };
  }, [open]);
  return estado;
}

function ElegirArbol({ valor, onChange, opciones, estado, etiqueta }) {
  return (
    <Select value={valor} onChange={onChange} aria-label={etiqueta} className="w-full"
      disabled={estado.cargando || (estado.error && !valor)}>
      <option value="">{estado.cargando ? 'Cargando árboles…' : estado.error ? 'No se pudieron cargar' : 'Sin árbol'}</option>
      {opciones.map((a) => (
        <option key={a.id} value={a.id}>{[a.codigo, a.sostiene].filter(Boolean).join(' · ')}{a.activo === false ? ' (dado de baja)' : ''}</option>
      ))}
    </Select>
  );
}

// Alta y edición de la ficha de un cable. onGuardar(datos) devuelve una
// promesa; si lanza, el error se muestra aquí y el formulario queda abierto.
export default function FormCable({ open, onClose, linea, instalacion = 'canopy', onGuardar }) {
  const hoy = hoyCR();
  const [form, setForm] = useState(() => lineaAFormulario(linea || { instalacion }));
  const [errores, setErrores] = useState({});
  const [error, setError] = useState('');
  const [enviando, setEnviando] = useState(false);
  const arboles = useArboles(open);

  useEffect(() => {
    if (!open) return;
    setForm(lineaAFormulario(linea || { instalacion }));
    setErrores({});
    setError('');
    setEnviando(false);
  }, [open, linea, instalacion]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  // Activos de la instalación elegida, más los que ya tenía la ficha.
  const opciones = useMemo(() => arboles.filas
    .filter((a) => ((a.instalacion || 'canopy') === form.instalacion && a.activo !== false)
      || a.id === form.arbol_desde_id || a.id === form.arbol_hasta_id)
    .sort(compararCodigo), [arboles.filas, form.instalacion, form.arbol_desde_id, form.arbol_hasta_id]);

  const guardar = async () => {
    if (enviando) return;
    const { datos, errores: e } = prepararLinea(form, hoy);
    setErrores(e);
    if (Object.keys(e).length) {
      setError('Revise los campos marcados.');
      return;
    }
    setEnviando(true);
    setError('');
    try {
      await onGuardar(datos);
      onClose();
    } catch (err) {
      setError(mensajeError(err, 'No se pudo guardar.'));
      setEnviando(false);
    }
  };

  return (
    <Modal open={open} onClose={enviando ? () => {} : onClose} title={linea ? `Editar cable ${linea.codigo}` : 'Registrar cable'}
      maxWidth="max-w-2xl" cerrarConFondo={false}
      footer={(
        <div className="flex gap-2">
          <Button type="button" variant="outline" size="lg" className="flex-1 px-4" onClick={onClose} disabled={enviando}>Cancelar</Button>
          <Button type="button" size="lg" className="flex-1 px-4" onClick={guardar} loading={enviando}>
            {linea ? 'Guardar cambios' : 'Registrar'}
          </Button>
        </div>
      )}>
      <div className="space-y-5">
        <Grupo titulo="Identificación">
          <Campo label="Código" requerido error={errores.codigo} ayuda="Por ejemplo C1, C2…">
            <Texto value={form.codigo} onChange={set('codigo')} data-autofocus autoCapitalize="characters" />
          </Campo>
          <Campo label="Instalación">
            <Select value={form.instalacion} onChange={set('instalacion')} aria-label="Instalación" className="w-full">
              {INSTALACIONES.map((i) => <option key={i.id} value={i.id}>{i.label}</option>)}
            </Select>
          </Campo>
          <Campo label="Nombre" ayuda="Opcional, por ejemplo: Cable del río" className="sm:col-span-2">
            <Texto value={form.nombre} onChange={set('nombre')} />
          </Campo>
        </Grupo>

        <Grupo titulo="Recorrido">
          <Campo label="Plataforma de salida">
            <Texto value={form.plataforma_desde} onChange={set('plataforma_desde')} placeholder="Plataforma 1" />
          </Campo>
          <Campo label="Plataforma de llegada">
            <Texto value={form.plataforma_hasta} onChange={set('plataforma_hasta')} placeholder="Plataforma 2" />
          </Campo>
          <Campo label="Árbol de salida">
            <ElegirArbol valor={form.arbol_desde_id} onChange={set('arbol_desde_id')} opciones={opciones} estado={arboles} etiqueta="Árbol de salida" />
          </Campo>
          <Campo label="Árbol de llegada" error={errores.arbol_hasta_id}>
            <ElegirArbol valor={form.arbol_hasta_id} onChange={set('arbol_hasta_id')} opciones={opciones} estado={arboles} etiqueta="Árbol de llegada" />
          </Campo>
          <Campo label="Longitud (m)" error={errores.longitud_m}>
            <Texto value={form.longitud_m} onChange={set('longitud_m')} inputMode="decimal" />
          </Campo>
        </Grupo>

        <Grupo titulo="Especificaciones técnicas">
          <Campo label="Diámetro (mm)" error={errores.diametro_mm}>
            <Texto value={form.diametro_mm} onChange={set('diametro_mm')} inputMode="decimal" />
          </Campo>
          <Campo label="Material" ayuda="Por ejemplo: acero galvanizado">
            <Texto value={form.material} onChange={set('material')} />
          </Campo>
          <Campo label="Construcción" ayuda="Por ejemplo: 7x19, alma de acero">
            <Texto value={form.construccion} onChange={set('construccion')} />
          </Campo>
          <Campo label="Carga de rotura (kN)" error={errores.carga_rotura_kn}>
            <Texto value={form.carga_rotura_kn} onChange={set('carga_rotura_kn')} inputMode="decimal" />
          </Campo>
          <Campo label="Tensión de diseño">
            <Texto value={form.tension_diseno} onChange={set('tension_diseno')} placeholder="Por ejemplo: 12 kN" />
          </Campo>
          <Campo label="Flecha de diseño">
            <Texto value={form.flecha_diseno} onChange={set('flecha_diseno')} placeholder="Por ejemplo: 2 %" />
          </Campo>
          <Campo label="Freno" className="sm:col-span-2">
            <Texto value={form.freno} onChange={set('freno')} placeholder="Tipo de freno al final del cable" />
          </Campo>
        </Grupo>

        <Grupo titulo="Proveniencia">
          <Campo label="Fabricante">
            <Texto value={form.fabricante} onChange={set('fabricante')} />
          </Campo>
          <Campo label="Proveedor">
            <Texto value={form.proveedor} onChange={set('proveedor')} />
          </Campo>
          <Campo label="Fecha de instalación" error={errores.fecha_instalacion}>
            <DatePicker value={form.fecha_instalacion} onChange={set('fecha_instalacion')} max={hoy} clearable
              aria-label="Fecha de instalación" className="w-full" />
          </Campo>
          <Campo label="Vida útil (años)" error={errores.vida_util_anios} ayuda="Según el fabricante">
            <Texto value={form.vida_util_anios} onChange={set('vida_util_anios')} inputMode="decimal" />
          </Campo>
        </Grupo>

        <Campo label="Notas">
          <Texto area rows={3} value={form.notas} onChange={set('notas')} />
        </Campo>
      </div>
      {linea && <p className="mt-3 text-xs text-muted-foreground">Cada cambio queda en el historial de la ficha con quién y cuándo.</p>}
      {error && <p role="alert" className="mt-3 rounded-lg bg-error/10 px-2.5 py-2 text-sm text-error">{error}</p>}
    </Modal>
  );
}
